import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, errorText, formatDate } from '../api';
import { useAuth } from '../auth';
import { EinsatzZeile, heute, kartenLink, plusTage, zeitfenster } from '../components/Einsatz';
import { Empty, Loading, Notice, kundenName } from '../components/ui';
import type { BenutzerKurz, Einsatz } from '../types';

const WOCHENTAG = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const montagVon = (datum: string) => {
  const d = new Date(`${datum}T12:00:00`);
  const versatz = (d.getDay() + 6) % 7;
  return plusTage(datum, -versatz);
};

/**
 * Einsätze: Woche (Überblick für die Planung) und Tag (Tour mit
 * Reihenfolge, Navigation und Pickliste). Fahrer und Techniker sehen nur ihre
 * eigenen Einsätze - das erledigt der Server.
 */
export function EinsaetzePage() {
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const ansicht = params.get('ansicht') ?? (can('admin', 'verkauf') ? 'woche' : 'tag');
  const datum = params.get('datum') ?? heute();
  const mitarbeiterId = params.get('mitarbeiter') ?? '';
  const [einsaetze, setEinsaetze] = useState<Einsatz[] | null>(null);
  const [benutzer, setBenutzer] = useState<BenutzerKurz[]>([]);
  const [fehler, setFehler] = useState('');
  const leitung = can('admin', 'verkauf');

  const von = ansicht === 'woche' ? montagVon(datum) : datum;
  const bis = ansicht === 'woche' ? plusTage(von, 6) : datum;

  useEffect(() => {
    if (leitung) void api<BenutzerKurz[]>('/api/benutzer/auswahl').then(setBenutzer);
  }, [leitung]);

  const laden = () => {
    setEinsaetze(null);
    api<Einsatz[]>('/api/einsaetze', { query: { von, bis, mitarbeiterId } })
      .then(setEinsaetze)
      .catch((e) => setFehler(errorText(e)));
  };
  useEffect(laden, [von, bis, mitarbeiterId]);

  const setze = (werte: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(werte)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  async function verschiebe(index: number, richtung: -1 | 1) {
    if (!einsaetze) return;
    const geplante = einsaetze.filter((e) => e.status === 'geplant');
    const ziel = index + richtung;
    if (ziel < 0 || ziel >= geplante.length) return;
    const neu = [...geplante];
    [neu[index], neu[ziel]] = [neu[ziel]!, neu[index]!];
    await api('/api/einsaetze/reihenfolge', { method: 'POST', body: { ids: neu.map((e) => e.id) } });
    laden();
  }

  const geplante = (einsaetze ?? []).filter((e) => e.status === 'geplant');
  // Route über alle Stopps in der gewählten Reihenfolge (Google Maps nimmt bis zu ~10 Wegpunkte).
  const route =
    geplante.length > 0
      ? `https://www.google.com/maps/dir/${geplante.map((e) => encodeURIComponent([e.strasse, e.plz, e.ort].filter(Boolean).join(', '))).join('/')}`
      : null;

  return (
    <>
      <div className="kopf nicht-drucken">
        <h1>Einsätze</h1>
        <span className="spacer" />
        <div className="tabs" style={{ marginBottom: 0, borderBottom: 0 }}>
          <button className={ansicht === 'tag' ? 'aktiv' : ''} onClick={() => setze({ ansicht: 'tag' })}>
            Tag / Tour
          </button>
          <button className={ansicht === 'woche' ? 'aktiv' : ''} onClick={() => setze({ ansicht: 'woche' })}>
            Woche
          </button>
        </div>
      </div>
      <div className="row nicht-drucken" style={{ marginBottom: 14 }}>
        <button onClick={() => setze({ datum: plusTage(datum, ansicht === 'woche' ? -7 : -1) })}>←</button>
        <input type="date" value={datum} onChange={(e) => setze({ datum: e.target.value })} style={{ width: 170 }} />
        <button onClick={() => setze({ datum: plusTage(datum, ansicht === 'woche' ? 7 : 1) })}>→</button>
        <button onClick={() => setze({ datum: '' })}>Heute</button>
        {leitung && (
          <select value={mitarbeiterId} onChange={(e) => setze({ mitarbeiter: e.target.value })} style={{ width: 200 }}>
            <option value="">Alle Mitarbeiter</option>
            {benutzer.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        )}
        <span className="spacer" />
        {ansicht === 'tag' && route && (
          <a className="button" href={route} target="_blank" rel="noreferrer">
            Route in Google Maps ↗
          </a>
        )}
        {ansicht === 'tag' && geplante.length > 0 && <button onClick={() => window.print()}>🖨 Tourliste / Pickliste</button>}
      </div>
      {fehler && <Notice kind="error">{fehler}</Notice>}

      {!einsaetze ? (
        <Loading />
      ) : ansicht === 'woche' ? (
        <div className="woche">
          {Array.from({ length: 7 }, (_, i) => plusTage(von, i)).map((tag) => {
            const am = einsaetze.filter((e) => e.datum === tag);
            const d = new Date(`${tag}T12:00:00`);
            return (
              <div key={tag} className={tag === heute() ? 'tag heute' : 'tag'}>
                <h3>
                  <button className="link" onClick={() => setze({ ansicht: 'tag', datum: tag })}>
                    {WOCHENTAG[d.getDay()]} {formatDate(tag).slice(0, 6)}
                  </button>
                </h3>
                {am.map((e) => (
                  <Link key={e.id} to={`/einsaetze/${e.id}`} className={`termin ${e.art === 'aussendienst' ? 'service' : ''} ${e.status === 'erledigt' ? 'erledigt' : ''}`}>
                    <strong>{e.zeit_von ? e.zeit_von.slice(0, 5) : '—'}</strong> {kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname, firma: e.kunde_firma })}
                    <div className="muted">
                      {e.ort} · {e.mitarbeiter.map((m) => m.name.split(' ')[0]).join(', ')}
                    </div>
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      ) : einsaetze.length === 0 ? (
        <Empty>Keine Einsätze an diesem Tag.</Empty>
      ) : (
        <>
          <div className="ticketliste nicht-drucken">
            {einsaetze.map((e) => {
              const i = geplante.findIndex((g) => g.id === e.id);
              return (
                <div key={e.id} className="row" style={{ flexWrap: 'nowrap', alignItems: 'stretch' }}>
                  {leitung && i >= 0 && (
                    <div className="stack" style={{ gap: 2 }}>
                      <button className="klein" disabled={i === 0} onClick={() => void verschiebe(i, -1)} aria-label="früher">
                        ▲
                      </button>
                      <button className="klein" disabled={i === geplante.length - 1} onClick={() => void verschiebe(i, 1)} aria-label="später">
                        ▼
                      </button>
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <EinsatzZeile e={e} />
                  </div>
                </div>
              );
            })}
          </div>
          {/* Druckansicht: Tourliste mit allem, was der Fahrer braucht, plus Pickliste. */}
          <div className="nur-drucken">
            <h1>Tour {formatDate(datum)}</h1>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Zeit</th>
                  <th>Kunde / Adresse</th>
                  <th>Was</th>
                  <th>Hinweis</th>
                </tr>
              </thead>
              <tbody>
                {geplante.map((e, i) => (
                  <tr key={e.id}>
                    <td>{i + 1}</td>
                    <td>{zeitfenster(e.zeit_von, e.zeit_bis)}</td>
                    <td>
                      {kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname, firma: e.kunde_firma })}
                      <br />
                      {[e.strasse, [e.plz, e.ort].filter(Boolean).join(' ')].filter(Boolean).join(', ')}
                      <br />
                      {(e.kunde_telefone ?? []).join(', ')}
                    </td>
                    <td>
                      {e.artikel ?? e.geraet}
                      {e.leistungen?.length ? <div>{e.leistungen.join(', ')}</div> : null}
                    </td>
                    <td>{[e.lieferhinweis, e.hinweis].filter(Boolean).join(' · ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h2 style={{ marginTop: 24 }}>Pickliste</h2>
            <ul>
              {geplante
                .filter((e) => e.artikel)
                .map((e) => (
                  <li key={e.id}>
                    ☐ {e.artikel} – {kundenName({ vorname: e.kunde_vorname, nachname: e.kunde_nachname, firma: e.kunde_firma })} ({e.lieferung_nummer})
                  </li>
                ))}
            </ul>
            <p className="small">Navigation: {geplante.map((e) => kartenLink(e.strasse, e.plz, e.ort)).length} Stopps</p>
          </div>
        </>
      )}
    </>
  );
}
